import type { SavedViewResponse } from '@domain/schemas/saved-view.schema';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { QueryFactory } from './factory.types';

export type SavedView = SavedViewResponse;

// Polling is the backstop for contract changes no client-side hook can observe (other
// users, editor saves); refetchInterval pauses in background tabs by default.
const SAVED_VIEWS_STALE_TIME_MS = 15 * 1000;
const SAVED_VIEWS_REFETCH_INTERVAL_MS = 30 * 1000;

export const fetchSavedViews = async (): Promise<SavedView[]> => {
  const networkPayload = await getRequestAuthPayload({ method: 'get', url: '/saved-views' });
  const response = await axios(networkPayload);
  return response.data;
};

export const savedViewQueries = {
  list: () =>
    queryOptions({
      queryKey: ['saved-views'] as const,
      queryFn: fetchSavedViews,
      staleTime: SAVED_VIEWS_STALE_TIME_MS,
    }),
} satisfies QueryFactory;

export const invalidateSavedViewCounts = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: savedViewQueries.list().queryKey });

export function useSavedViews(enabled = true, poll = false) {
  const queryClient = useQueryClient();
  const list = savedViewQueries.list();
  const { data, isFetching } = useQuery({
    ...list,
    enabled,
    refetchInterval: poll && SAVED_VIEWS_REFETCH_INTERVAL_MS,
  });
  const isRefreshing = isFetching && (queryClient.getQueryState(list.queryKey)?.isInvalidated ?? false);
  return { data, isRefreshing };
}
